import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox'; // Import SearchableSelectBox
import Checkbox from '../components/ui/Checkbox'; // Import Checkbox component
import { useToast } from '../hooks/useToast';
import { DollarSign, CreditCard, Loader2, Info, XCircle } from 'lucide-react';
import { fetchStores, fetchUtilCustomers, currencyCodeToSymbolMap, fetchCustomerStoreLOC } from '../apiHelper'; // Import API helpers and currency map
import { client } from '../apiHelper';
/**
 * Creditline Component
 * This component is used to manage or display a customer's line of credit.
 * It provides a form to attach/update a new one, allowing selection of
 * customer and store via searchable dropdowns.
 */
const Creditline = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const location = useLocation(); // To get state passed from navigation

    // State for logged-in user
    const [loggedInUser, setLoggedInUser] = useState(null);

    // Data for searchable select boxes
    const [stores, setStores] = useState([]);
    const [customers, setCustomers] = useState([]);

    // Selected entities (full objects)
    const [selectedStore, setSelectedStore] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // Form states for credit line details
    const [creditLimit, setCreditLimit] = useState('');
    const [outstandingBalance, setOutstandingBalance] = useState('');
    const [isActive, setIsActive] = useState(true); // Changed from creditStatus to isActive (boolean)

    // Loading and error states for data fetching and form submission
    const [loadingInitialData, setLoadingInitialData] = useState(true);
    const [errorInitialData, setErrorInitialData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [existingCreditLine, setExistingCreditLine] = useState(null);
    const [isEditingExisting, setIsEditingExisting] = useState(false);

    // Derived state for currency symbol
    const selectedStoreCurrencySymbol = useMemo(() => {
        return selectedStore?.setccurr ? currencyCodeToSymbolMap[selectedStore.setccurr] || selectedStore.setccurr : '₦';
    }, [selectedStore]);


    // Function to reset the form fields to their initial state
    const resetForm = useCallback(() => {
        setCreditLimit('');
        setOutstandingBalance('');
        setIsActive(true); // Default to active
        setSelectedCustomer(null);
        setSelectedStore(null);
    }, []);

    // Effect to load user from local storage and all necessary dropdown options
    useEffect(() => {
        const loadAllData = async () => {
            setLoadingInitialData(true);
            setErrorInitialData(null);
            const lcUser = localStorage.getItem("user");
            let parsedUser = null;

            if (lcUser) {
                parsedUser = JSON.parse(lcUser);
                setLoggedInUser(parsedUser);
            } else {
                showToast("Please log in to manage credit lines.", "info");
                setLoadingInitialData(false);
                setErrorInitialData("Authentication required.");
                navigate('/login'); // Consider redirecting if not logged in
                return;
            }

            try {
                const [fetchedStores, fetchedCustomers] = await Promise.all([
                    fetchStores(parsedUser),
                    fetchUtilCustomers(parsedUser, '') // Fetch all customers
                ]);

                setStores(fetchedStores);
                setCustomers(fetchedCustomers);

                // Attempt to pre-select customer if ID is passed via route state
                const initialCustomerId = location.state?.customerId;
                if (initialCustomerId) {
                    const foundCustomer = fetchedCustomers.find(cust => cust.member_id === initialCustomerId);
                    if (foundCustomer) {
                        // console.log(foundCustomer)
                        setSelectedCustomer(foundCustomer);
                        showToast(`Managing credit line for ${foundCustomer.user?.first_name || ''} ${foundCustomer.user?.last_name || ''}`, 'info');
                    }
                }

                // Placeholder: In a real application, if a customer is selected,
                // you would fetch their existing credit line data here.
                // For now, we'll simulate loading and setting default values.
                if (initialCustomerId) {
                    // console.log(initialCustomerId)
                    // Simulate API call for existing credit line
                    setTimeout(() => {
                        setCreditLimit('5000.00');
                        setOutstandingBalance('0.00');
                        setIsActive(true); // Set to true for 'Active' status
                    }, 500);
                }

            } catch (err) {
                console.error("Error loading initial data for Creditline:", err);
                const errorMessage = err.response?.data?.detail || `Failed to load necessary data: ${err.message || err}`;
                setErrorInitialData(errorMessage);
                showToast(errorMessage, "error");
            } finally {
                setLoadingInitialData(false);
            }
        };

        loadAllData();
    }, [showToast, location.state?.customerId]); // Re-run if customerId in state changes

    useEffect(() => {
        const loadExistingCreditLine = async () => {
            if (!selectedCustomer || !selectedStore || !loggedInUser) {
                setExistingCreditLine(null);
                setIsEditingExisting(false);
                return;
            }

            try {
                const payload = {
                    store_id: selectedStore.store_id,
                    customer_id: selectedCustomer.member_id,
                    referer_id: loggedInUser.users_id,
                };

                const data = await fetchCustomerStoreLOC(payload);

                if (data && data.creditline_id) {
                    // console.log(data)
                    setExistingCreditLine(data);
                    setIsEditingExisting(true);

                    // Pre-populate form
                    setCreditLimit(data.credit_limit?.toString() || '');
                    setOutstandingBalance(data.current_balance?.toString() || '0');
                    setIsActive(data.is_active ?? true);

                    showToast('Existing credit line loaded.', 'info');
                } else {
                    // No existing LOC → reset to new
                    setExistingCreditLine(null);
                    setIsEditingExisting(false);
                    setCreditLimit('');
                    setOutstandingBalance('0');
                    setIsActive(true);
                }
            } catch (err) {
                console.error('Failed to fetch existing credit line:', err);
                // Assume no existing LOC on error
                setExistingCreditLine(null);
                setIsEditingExisting(false);
                setCreditLimit('');
                setOutstandingBalance('0');
                setIsActive(true);
            }
        };

        loadExistingCreditLine();
    }, [selectedCustomer, selectedStore, loggedInUser, showToast]);

    // Handlers for form input changes
    const handleCreditLimitChange = (e) => setCreditLimit(e.target.value);
    const handleOutstandingBalanceChange = (e) => setOutstandingBalance(e.target.value);
    const handleIsActiveChange = (e) => setIsActive(e.target.checked); // Handler for checkbox

    // Handle customer selection from SearchableSelectBox
    const handleCustomerSelect = useCallback((value) => {
        const customerId = parseInt(value, 10);
        const customer = customers.find(c => c.member_id === customerId);
        setSelectedCustomer(customer);
        // If a customer is selected, you might want to load their existing credit line data here
        // For now, we'll reset or keep existing form values.
    }, [customers]);

    // Handle store selection from SearchableSelectBox
    const handleStoreSelect = useCallback((value) => {
        const storeId = parseInt(value, 10);
        const store = stores.find(s => s.store_id === storeId);
        setSelectedStore(store);
    }, [stores]);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrorInitialData(null);

        // --- Validation ---
        if (!selectedCustomer) {
            setErrorInitialData("Please select a Customer.");
            showToast('Customer is required.', 'warning');
            setIsSubmitting(false);
            return;
        }
        if (!selectedStore) {
            setErrorInitialData("Please select a Branch.");
            showToast('Branch is required.', 'warning');
            setIsSubmitting(false);
            return;
        }
        if (!creditLimit || parseFloat(creditLimit) <= 0 || isNaN(parseFloat(creditLimit))) {
            setErrorInitialData("Credit Limit must be a positive number.");
            showToast('Credit Limit must be a positive number.', 'warning');
            setIsSubmitting(false);
            return;
        }
        // --- End Validation ---

        try {
            // Base Payload
            const payload = {
                customer_id: selectedCustomer.member_id,
                store_id: selectedStore.store_id,
                credit_limit: parseFloat(creditLimit),
                outstanding_balance: parseFloat(outstandingBalance || 0),
                is_active: isActive,
                creator_id: loggedInUser?.users_id,
            };

            let url;
            let method;
            let successMessage;

            if (isEditingExisting && existingCreditLine?.creditline_id) {
                // 🚨 CRITICAL MODIFICATION: Add creditline_id to the payload 🚨
                payload.creditline_id = existingCreditLine.creditline_id;

                url = `/sales/update-credit-line`;
                method = client.postData;
                successMessage = 'Credit line updated successfully!';
                // console.log(payload)
            } else {
                // Create new
                url = '/sales/create-credit-line';
                method = client.postData;
                successMessage = 'Credit line created successfully!';
                // console.log(payload)
            }

            // Execute the API call
            console.log(payload)
            const response = await method(url, payload);
            showToast(response.msg || successMessage, 'success');

            // Optional: refetch or just reset if needed
        } catch (err) {
            const errorMessage = err.response?.data?.detail || 'Failed to save credit line';
            setErrorInitialData(errorMessage);
            showToast(errorMessage, 'error');
        } finally {
            setIsSubmitting(false);
        }
    }, [selectedCustomer, selectedStore, creditLimit, outstandingBalance, isActive, loggedInUser, isEditingExisting, existingCreditLine, showToast]);

    // Memoized options for SearchableSelectBoxes
    const customerOptions = useMemo(() => customers.map(cust => ({
        label: `${cust.user?.first_name || ''} ${cust.user?.last_name || ''} (${cust.user?.credential?.logon_id || 'N/A'})`,
        value: cust.member_id,
    })), [customers]);

    const storeOptions = useMemo(() => stores.map(store => ({
        label: store.identifier,
        value: store.store_id,
    })), [stores]);


    if (!loggedInUser) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">You must be logged in to manage credit lines.</p>
            </div>
        );
    }

    if (loadingInitialData) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Loader2 size={32} className="animate-spin text-blue-500 mr-3" />
                <p className="text-text-secondary text-lg">Loading initial data...</p>
            </div>
        );
    }

    if (errorInitialData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4">
                <XCircle size={32} className="mb-2 text-red-500" />
                <p className="text-gmail-red text-lg mb-4">Error: {errorInitialData}</p>
                <Button onClick={() => window.location.reload()} variant="primary">
                    Reload Page
                </Button>
            </div>
        );
    }

    // Check if essential data is missing after loading
    const essentialDataMissing = customerOptions.length === 0 || storeOptions.length === 0;

    if (essentialDataMissing) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-4">
                <p className="text-gmail-red text-lg mb-4">
                    Missing essential data (Customers or Stores). Please ensure these are created first.
                </p>
                {/* Potentially add buttons to navigate to customer/store creation pages */}
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Form */}
                <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-6 max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                    <div className="mb-6">
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">Manage Customer Credit Line</h1>
                        <p className="text-gray-600 text-md">Configure or view the line of credit for a selected customer.</p>
                    </div>

                    <form onSubmit={handleSubmit} noValidate>
                        {/* Customer Details Section */}
                        <div className="mb-8 border-b border-gray-200 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Customer Details</h2>
                            <SearchableSelectBox
                                label="Select Customer"
                                id="customer_id"
                                name="customer_id"
                                value={selectedCustomer?.member_id || ''}
                                onChange={(e) => handleCustomerSelect(e.target.value)}
                                options={customerOptions}
                                placeholder="Search and select a customer"
                                required
                                error={errorInitialData && !selectedCustomer ? "Customer is required." : ""}
                            />
                            {selectedCustomer && (
                                <div className="mt-2 text-sm text-gray-600">
                                    <p>Email: {selectedCustomer.user?.credential?.logon_id || 'N/A'}</p>
                                    <p>Type: {selectedCustomer.user?.profile_type === 'C' ? 'Customer' : 'Other'}</p>
                                </div>
                            )}
                        </div>

                        {/* Credit Line Information Section */}
                        <div className="mb-8 pb-6">
                            <h2 className="text-xl font-semibold text-gray-700 mb-4">Credit Line Information</h2>
                            <SearchableSelectBox
                                label="Select Branch"
                                id="store_id"
                                name="store_id"
                                value={selectedStore?.store_id || ''}
                                onChange={(e) => handleStoreSelect(e.target.value)}
                                options={storeOptions}
                                placeholder="Select the branch for this credit line"
                                required
                                error={errorInitialData && !selectedStore ? "Branch is required." : ""}
                                containerClassName="mb-4"
                            />
                            <Input
                                label="Credit Limit"
                                id="creditLimit"
                                name="creditLimit"
                                type="number"
                                step="0.01"
                                placeholder={`e.g., ${selectedStoreCurrencySymbol} 500000.00`}
                                value={creditLimit}
                                onChange={handleCreditLimitChange}
                                required
                                icon={<DollarSign size={18} className="text-gray-dark" />}
                                // Display error if it's related to creditLimit validation
                                error={errorInitialData && errorInitialData.includes("Credit Limit") ? errorInitialData : ""}
                            />

                            <Input
                                label="Outstanding Balance"
                                id="outstandingBalance"
                                name="outstandingBalance"
                                type="number"
                                step="0.01"
                                readOnly={true}
                                placeholder={`e.g., ${selectedStoreCurrencySymbol} 150000.00`}
                                value={outstandingBalance}
                                onChange={handleOutstandingBalanceChange}
                                icon={<DollarSign size={18} className="text-gray-dark" />}
                            />

                            <Checkbox
                                label={isActive ? "Credit Line is Active" : "Credit Line is Suspended"}
                                id="isActive"
                                name="isActive"
                                checked={isActive}
                                onChange={handleIsActiveChange}
                                containerClassName="mb-4"
                            />

                            {/* Last Review Date field removed */}
                        </div>

                        {errorInitialData && !errorInitialData.includes("Credit Limit") && ( // Display general errors not specific to credit limit
                            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                                <p>{errorInitialData}</p>
                            </div>
                        )}

                        <div className="flex justify-end gap-3 mt-6">
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={() => navigate('/users')}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isSubmitting}
                                className="flex items-center justify-center py-4"
                            >
                                {isSubmitting ? (
                                    <Loader2 className="animate-spin h-5 w-5 mr-2" />
                                ) : (
                                    <CreditCard size={20} className="mr-2" />
                                )}
                                {isSubmitting ? 'Saving...' : 'Save Credit Line'}
                            </Button>
                        </div>
                    </form>
                </div>

                {/* Right Column: Placeholder for potential Credit Line Summary/History */}
                <div className="lg:col-span-1 bg-white rounded-lg shadow-md p-6 max-h-[90vh] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                    <h2 className="text-xl font-semibold text-gray-700 mb-4">Credit Line Summary</h2>
                    <p className="text-gray-500 text-sm mb-4">
                        Details of the selected customer's credit history will appear here.
                    </p>
                    {selectedCustomer ? (
                        <div className="space-y-3 text-text-primary text-sm">
                            <p><strong>Customer:</strong> {selectedCustomer.user?.first_name} {selectedCustomer.user?.last_name}</p>
                            <p><strong>Email:</strong> {selectedCustomer.user?.credential?.logon_id}</p>
                            <p><strong>Current Limit:</strong> {selectedStoreCurrencySymbol} {parseFloat(creditLimit || 0).toFixed(2)}</p>
                            <p><strong>Balance:</strong> {selectedStoreCurrencySymbol} {parseFloat(outstandingBalance || 0).toFixed(2)}</p>
                            <p><strong>Status:</strong> {isActive ? 'Active' : 'Suspended'}</p>
                            {/* Last Review removed from here */}
                        </div>
                    ) : (
                        <p className="text-gray-400">Select a customer to view summary.</p>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Creditline;
